import {domToReact, Element as ParserElement, HTMLReactParserOptions, DOMNode} from 'html-react-parser';
import {Fragment, useCallback, useContext, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Button, ClientDynamicContent, FaceRating, NodeElement, PredefinedNotifierType, StarRating, TextArea} from 'components';
import {DynamicComponentNames, FEEDBACK_DEFAULT_TEMPLATE, FeedbackUnits as Units} from 'constants/client';
import {FEEDBACK_COMMENT_MAX_LENGTH} from 'constants/session';
import {ClientContext} from 'context';
import {FeedbackView as FeedbackViewEnum} from 'enums';
import {useApiData, useClientSession} from 'hooks';
import {ClientDynamicLayout} from 'layouts';
import {TRANSLATION_KEYS} from 'locale';
import {dynamicContentUtils} from 'utils';

import './styles.scss';
import {FeedbackSubmitted} from './FeedbackSubmitted';

const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;
const SESSION_T_KEYS = COMMON_T_KEYS.SESSION;

const {isMatchElement, renderDefaultNode} = dynamicContentUtils;
const getOptions = (props: any): HTMLReactParserOptions => {
    const {
        t, feedbackRatingEnabled, feedbackCommentEnabled, handleCancel, handleSubmit, renderRatingInput, renderCommentInput, renderForm,
        renderCancel, renderSubmit, submitDisabled, localParticipant
    } = props;
    return {
        replace: (domNode: any) => {
            if (!domNode.attribs) {
                return null;
            }

            const node = domNode as ParserElement;

            if (isMatchElement(Units.CancelButton, node)) {
                return <NodeElement node={node} onClick={handleCancel}>{domToReact(node.children as DOMNode[])}</NodeElement>;
            }
            if (isMatchElement(Units.CancelButtonDefault, node)) {
                return <NodeElement node={node}>{renderCancel()}</NodeElement>;
            }
            if (isMatchElement(Units.CommentInputContainer, node)) {
                return <NodeElement node={node} show={feedbackCommentEnabled}>{renderCommentInput()}</NodeElement>;
            }
            if (isMatchElement(Units.FormDefault, node)) {
                return <NodeElement node={node}>{renderForm()}</NodeElement>;
            }
            if (isMatchElement(Units.RatingInputContainer, node)) {
                return <NodeElement node={node} show={feedbackRatingEnabled}>{renderRatingInput()}</NodeElement>;
            }
            if (isMatchElement(Units.SubmitButton, node)) {
                return (
                    <NodeElement node={node} disabled={submitDisabled} onClick={handleSubmit}>
                        {domToReact(node.children as DOMNode[])}
                    </NodeElement>);
            }
            if (isMatchElement(Units.SubmitButtonDefault, node)) {
                return <NodeElement node={node}>{renderSubmit()}</NodeElement>;
            }
            if (isMatchElement(Units.Title, node)) {
                return <NodeElement node={node}>{t(SESSION_T_KEYS.FEEDBACK.ADD_TITLE)}</NodeElement>;
            }
            return renderDefaultNode(node, {role: localParticipant.role});
        }
    };
};

type Props = {
    onClose: () => void;
};

export const FeedbackView: React.FC<Props> = ({onClose}: Props) => {
    const {t} = useTranslation();
    const {addFeedback} = useContext(ClientContext);
    const {
        image, feedbackRatingEnabled, feedbackCommentEnabled, feedbackRatingView, feedbackRatingLabels, feedbackRatingDirection
    } = useClientSession();
    const [rating, setRating] = useState<number>(null);
    const [comment, setComment] = useState<string>('');
    const [submitted, setSubmitted] = useState(false);

    const onSendFeedbackSuccess = useCallback(() => {
        setSubmitted(true);
    }, []);

    const {send: sendFeedback, loading} = useApiData(addFeedback,
        {onSuccess: onSendFeedbackSuccess, notificationType: PredefinedNotifierType.SendError});

    const submitDisabled = useMemo(() => loading || (!rating && !comment), [loading, rating, comment]);

    const handleCancel = useCallback(() => {
        onClose();
    }, [onClose]);

    const handleSubmit = useCallback(() => {
        sendFeedback({
            ...(feedbackRatingEnabled && {rating}),
            ...(feedbackCommentEnabled && {comment}),
        });
    }, [feedbackRatingEnabled, feedbackCommentEnabled, rating, comment, sendFeedback]);

    const renderRatingInput = useCallback(() => {
        if (feedbackRatingEnabled) {
            if (feedbackRatingView === FeedbackViewEnum.Faces) {
                return (
                    <FaceRating
                        value={rating}
                        size='large'
                        className='tw-mt-6'
                        labels={feedbackRatingLabels}
                        direction={feedbackRatingDirection}
                        onChange={setRating}
                    />
                );
            } else if (feedbackRatingView === FeedbackViewEnum.Stars) {
                return (
                    <StarRating
                        value={rating}
                        size='large'
                        onChange={setRating}
                    />
                );
            }
        }
    }, [feedbackRatingEnabled, feedbackRatingView, rating, feedbackRatingLabels, feedbackRatingDirection]);

    const renderCommentInput = useCallback(() => (
        feedbackCommentEnabled &&
            <TextArea
                value={comment}
                disabled={loading}
                label={t(TRANSLATION_KEYS.COMMON.SESSION.LABELS.COMMENTS)}
                rows={4}
                maxLength={FEEDBACK_COMMENT_MAX_LENGTH}
                className='comment-textarea'
                onChange={setComment}
            />
    ), [t, feedbackCommentEnabled, loading, comment]);

    const renderCancelButton = useCallback(() => (
        <Button
            width='small'
            color='default'
            onClick={handleCancel}
        >
            {t(COMMON_T_KEYS.BUTTONS.CANCEL)}
        </Button>
    ), [t, handleCancel]);

    const renderSubmitButton = useCallback(() => (
        <Button
            color='primary'
            width='small'
            disabled={submitDisabled}
            onClick={handleSubmit}
        >
            {t(COMMON_T_KEYS.BUTTONS.OK)}
        </Button>
    ), [t, submitDisabled, handleSubmit]);

    const renderForm = useCallback(() => (
        <Fragment>
            <div className='controls'>
                {renderRatingInput()}
                {renderCommentInput()}
            </div>
            <div className='actions'>
                {renderCancelButton()}
                {renderSubmitButton()}
            </div>
        </Fragment>
    ), [renderRatingInput, renderCommentInput, renderCancelButton, renderSubmitButton]);

    return (
        <ClientDynamicLayout image={image}>
            {!submitted && <ClientDynamicContent
                component={DynamicComponentNames.Feedback}
                defaultTemplate={FEEDBACK_DEFAULT_TEMPLATE}
                optionsProps={{
                    submitDisabled, feedbackRatingEnabled, feedbackCommentEnabled, t, handleCancel, handleSubmit,
                    renderRatingInput, renderCommentInput, renderForm, renderCancelButton, renderSubmitButton
                }}
                optionsGetter={getOptions}
            />}
            {submitted && <FeedbackSubmitted />}
        </ClientDynamicLayout>
    );
};
